<template>
  <div
    v-if="show"
    class="fixed z-[9999] inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center p-0 sm:p-4"
  >
    <div
      class="bg-white w-full sm:max-w-4xl max-h-screen sm:max-h-[95vh] sm:rounded-lg border-0 sm:border border-gray-200 relative animate-fade-in flex flex-col"
    >
      <!-- Modal Loading Overlay -->
      <div
        v-if="isSaving"
        class="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center sm:rounded-lg"
      >
        <div class="text-center">
          <div
            class="w-6 h-6 sm:w-8 sm:h-8 border-3 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-2"
          ></div>
          <p class="text-xs sm:text-sm text-gray-600">Saving changes...</p>
        </div>
      </div>
      <!-- Fixed Header -->
      <div
        class="flex-shrink-0 p-4 sm:p-6 md:p-8 border-b border-gray-200 bg-white"
      >
        <!-- Close Button -->
        <button
          @click="$emit('close')"
          :disabled="isSaving"
          class="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation z-10"
          title="Close"
        >
          <X class="w-5 h-5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
        </button>
        <!-- Header -->
        <div class="flex items-center gap-2 sm:gap-3 pr-10 sm:pr-12">
          <div
            class="p-2 sm:p-3 bg-green-600 rounded-lg border border-green-600 flex-shrink-0"
          >
            <Pencil class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div class="flex-1 min-w-0">
            <h2
              class="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate"
            >
              Edit Student
            </h2>
            <p class="text-gray-500 text-xs sm:text-sm">
              Update student information and documents
            </p>
          </div>
        </div>
      </div>
      <!-- Scrollable Form Content -->
      <div class="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
        <!-- Error Message -->
        <div
          v-if="saveError"
          class="mb-3 sm:mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 sm:gap-3"
        >
          <div class="p-1 bg-red-100 rounded-full flex-shrink-0">
            <svg
              class="w-3 h-3 sm:w-4 sm:h-4 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-semibold text-red-800">Error</h4>
            <p class="text-sm text-red-700 mt-1 break-words">
              {{ saveError }}
            </p>
          </div>
          <button
            @click="$emit('clear-error')"
            class="p-1 text-red-400 hover:text-red-600 transition-colors flex-shrink-0 touch-manipulation"
          >
            <svg
              class="w-3 h-3 sm:w-4 sm:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <!-- Success Message -->
        <div
          v-if="saveSuccess"
          class="mb-3 sm:mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 sm:gap-3"
        >
          <div class="p-1 bg-green-100 rounded-full flex-shrink-0">
            <svg
              class="w-3 h-3 sm:w-4 sm:h-4 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-semibold text-green-800">Success</h4>
            <p class="text-sm text-green-700 mt-1 break-words">
              {{ saveSuccess }}
            </p>
          </div>
        </div>
        <!-- Form Sections -->
        <div class="space-y-6 sm:space-y-8">
          <!-- Basic Information Section -->
          <div class="bg-gray-50 rounded-lg p-3 sm:p-4 md:p-6">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                ></path>
              </svg>
              <span>Basic Information</span>
            </h3>
            <div
              class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6"
            >
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Full Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="student.name"
                  :disabled="isSaving"
                  :class="[
                    'w-full rounded-lg border-2 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation',
                    saveError && !student.name?.trim()
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                  placeholder="Enter full name"
                />
              </div>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Roll Number <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="student.rollNumber"
                  :disabled="isSaving"
                  :class="[
                    'w-full rounded-lg border-2 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation',
                    saveError && !student.rollNumber?.trim()
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                  placeholder="Enter roll number"
                />
              </div>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Email Address <span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="student.email"
                  type="email"
                  :disabled="isSaving"
                  :class="[
                    'w-full rounded-lg border-2 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation',
                    saveError &&
                    student.email?.trim() &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(student.email)
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200',
                  ]"
                  placeholder="Enter email address"
                />
              </div>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Primary Number<span class="text-red-500">*</span>
                </label>
                <input
                  v-model="student.phone"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter phone number"
                />
              </div>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Secondary Number<span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="student.secondaryPhone"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter secondary number"
                />
              </div>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Class<span class="text-red-500">*</span>
                </label>
                <select
                  v-model="student.class._id"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium bg-white text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  <option
                    v-for="cls in availableClasses"
                    :key="cls._id"
                    :value="cls._id"
                  >
                    {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                  </option>
                </select>
              </div>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Status<span class="text-red-500">*</span>
                </label>
                <select
                  v-model="student.status"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium bg-white text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  <option value="active">Active</option>
                  <option value="leaved">InActive</option>
                  <option value="passout">Passout</option>
                </select>
              </div>
              <!-- 👇 Extra fields only for passout -->
              <template v-if="student.status === 'passout'">
                <div class="space-y-2">
                  <label
                    class="block text-xs sm:text-sm font-semibold text-gray-700"
                  >
                    Passout Session<span class="text-green-500"
                      >(optional)</span
                    >
                  </label>
                  <input
                    v-model="student.session"
                    type="text"
                    placeholder="e.g. Winter / Summer"
                    :disabled="isSaving"
                    class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  />
                </div>

                <div class="space-y-2">
                  <label
                    class="block text-xs sm:text-sm font-semibold text-gray-700"
                  >
                    Passout Year<span class="text-red-500"
                      >-If empty then it applies the <b>current year</b></span
                    >
                  </label>
                  <input
                    v-model="student.passoutYear"
                    type="number"
                    placeholder="e.g. 2024"
                    :disabled="isSaving"
                    class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  />
                </div>
              </template>
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Enrollment Date<span class="text-red-500">*</span>
                </label>
                <input
                  v-model="student.enrollmentDate"
                  type="date"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
              </div>
              <!-- Date of Birth -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Date of Birth<span class="text-green-500">(optional)</span>
                </label>

                <input
                  v-model="student.dob"
                  type="date"
                  :max="new Date().toISOString().split('T')[0]"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
              </div>
              <!-- Location Fields -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  District <span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="student.subDistrict"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter sub district"
                />
              </div>

              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  City <span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="student.city"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter city"
                />
              </div>

              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  State <span class="text-green-500">(optional)</span>
                </label>
                <input
                  v-model="student.state"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter state"
                />
              </div>

              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Pincode <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="student.pincode"
                  type="text"
                  maxlength="6"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter pincode"
                />
              </div>

              <div class="md:col-span-2 space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Detailed Address<span class="text-green-500">(optional)</span>
                </label>
                <textarea
                  v-model="student.address"
                  rows="3"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-green-100 focus:border-green-500 focus:outline-none transition-all duration-200 font-medium resize-none text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                  placeholder="Enter full address"
                ></textarea>
              </div>
            </div>
          </div>
          <!-- Photo Section -->
          <div class="bg-blue-50 rounded-lg p-3 sm:p-4 md:p-6">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              <span>Student Photo</span>
            </h3>
            <div class="space-y-3 sm:space-y-4">
              <!-- Current Photo Display -->
              <div
                v-if="student.photo"
                class="flex items-center gap-3 sm:gap-4"
              >
                <img
                  :src="student.photo"
                  alt="Student Photo"
                  class="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover border border-gray-300 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-700">Current Photo</p>
                  <button
                    @click="
                      student.photo = null;
                      $emit('photo-upload', null);
                    "
                    :disabled="isSaving"
                    class="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 touch-manipulation"
                  >
                    Remove Photo
                  </button>
                </div>
              </div>
              <!-- Photo Upload -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Upload New Photo
                  <span class="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  @change="$emit('photo-upload', $event)"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
                <p class="text-xs text-gray-500">
                  Supported formats: JPG, PNG, GIF (Max: 5MB)
                </p>
              </div>
            </div>
          </div>
          <!-- Documents Section -->
          <div class="bg-yellow-50 rounded-lg p-3 sm:p-4 md:p-6">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              <span>Documents</span>
            </h3>
            <div
              class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6"
            >
              <!-- Aadhaar Card -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Aadhaar Card
                  <span class="text-gray-500 font-normal">(optional)</span>
                </label>
                <div
                  v-if="student.aadhaarCard"
                  class="mb-2"
                >
                  <div
                    class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded"
                  >
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span class="text-xs sm:text-sm text-green-700 flex-1"
                      >Document uploaded</span
                    >
                    <button
                      @click="student.aadhaarCard = ''"
                      :disabled="isSaving"
                      class="text-red-600 hover:text-red-800 disabled:opacity-50 flex-shrink-0 touch-manipulation"
                    >
                      <svg
                        class="w-3 h-3 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'aadhaarCard')"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-yellow-100 focus:border-yellow-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
              </div>
              <!-- Birth Certificate -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Birth Certificate
                  <span class="text-gray-500 font-normal">(optional)</span>
                </label>
                <div
                  v-if="student.birthCertificate"
                  class="mb-2"
                >
                  <div
                    class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded"
                  >
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span class="text-xs sm:text-sm text-green-700 flex-1"
                      >Document uploaded</span
                    >
                    <button
                      @click="student.birthCertificate = ''"
                      :disabled="isSaving"
                      class="text-red-600 hover:text-red-800 disabled:opacity-50 flex-shrink-0 touch-manipulation"
                    >
                      <svg
                        class="w-3 h-3 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'birthCertificate')"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-yellow-100 focus:border-yellow-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
              </div>
              <!-- Transfer Certificate -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Transfer Certificate
                  <span class="text-gray-500 font-normal">(optional)</span>
                </label>
                <div
                  v-if="student.transferCertificate"
                  class="mb-2"
                >
                  <div
                    class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded"
                  >
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span class="text-xs sm:text-sm text-green-700 flex-1"
                      >Document uploaded</span
                    >
                    <button
                      @click="student.transferCertificate = ''"
                      :disabled="isSaving"
                      class="text-red-600 hover:text-red-800 disabled:opacity-50 flex-shrink-0 touch-manipulation"
                    >
                      <svg
                        class="w-3 h-3 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="
                    $emit('document-upload', $event, 'transferCertificate')
                  "
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-yellow-100 focus:border-yellow-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
              </div>
              <!-- Marksheet -->
              <div class="space-y-2">
                <label
                  class="block text-xs sm:text-sm font-semibold text-gray-700"
                >
                  Marksheet
                  <span class="text-gray-500 font-normal">(optional)</span>
                </label>
                <div
                  v-if="student.marksheet"
                  class="mb-2"
                >
                  <div
                    class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded"
                  >
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span class="text-xs sm:text-sm text-green-700 flex-1"
                      >Document uploaded</span
                    >
                    <button
                      @click="student.marksheet = ''"
                      :disabled="isSaving"
                      class="text-red-600 hover:text-red-800 disabled:opacity-50 flex-shrink-0 touch-manipulation"
                    >
                      <svg
                        class="w-3 h-3 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  @change="$emit('document-upload', $event, 'marksheet')"
                  :disabled="isSaving"
                  class="w-full rounded-lg border-2 border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-yellow-100 focus:border-yellow-500 focus:outline-none transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                />
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-3 sm:mt-4">
              Supported formats: PDF, JPG, PNG (Max: 10MB per file)
            </p>
          </div>
        </div>
      </div>
      <!-- Fixed Footer -->
      <div
        class="flex-shrink-0 p-4 sm:p-6 md:p-8 border-t border-gray-200 bg-white"
      >
        <div
          class="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 md:gap-4"
        >
          <button
            @click="$emit('close')"
            :disabled="isSaving"
            class="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 touch-manipulation min-h-[44px] sm:min-h-[40px]"
          >
            Cancel
          </button>
          <button
            @click="$emit('save')"
            :disabled="isSaving"
            class="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed border border-green-600 touch-manipulation min-h-[44px] sm:min-h-[40px]"
          >
            <div
              v-if="isSaving"
              class="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            ></div>
            <Save
              v-else
              class="w-3 h-3 sm:w-4 sm:h-4"
            />
            <span>{{ isSaving ? "Saving..." : "Save Changes" }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from "vue";

  import { Pencil, X, Save } from "lucide-vue-next";
  import { useStore } from "vuex"; // Assuming Vuex for store management

  const store = useStore();

  // eslint-disable-next-line no-unused-vars
  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Object,
      default: () => ({}),
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    saveError: {
      type: String,
      default: "",
    },
    saveSuccess: {
      type: String,
      default: "",
    },
  });

  defineEmits([
    "close",
    "save",
    "photo-upload",
    "document-upload",
    "clear-error",
  ]);

  const availableClasses = ref(computed(() => store.getters.allClasses || []));

  onMounted(() => {
    store.dispatch("fetchAllClasses");
  });
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
